import { attachmentSchema } from '@qapline/core';
import { ECommissionState } from '@qapline/core/commission';
import { isTimeWindowOrdered, zodTimeString } from '@qapline/helpers';
import { T } from '@qapline/translations';
import { Currency } from 'modules/common';
import { locationCustomerSchema } from 'modules/customers';
import { AdminOfferStatusValue, EnquirySchema } from 'modules/offers/types';
import { z } from 'zod';

export { ECommissionState };

// TODO: Update according to actual values.

export type PostEnquiryEmailResponse = {
    sentStates: Record<string, boolean>;
    enquiry: EnquirySchema;
};

export type DispatcherSearchParameters = {
    minLength: number;
    minHeight: number;
    minWeight: number;
    minWidth: number;
    requiredFeatures: number[];
    requiredFeaturesSome: number[];
    vehicleTypes: number[];
};

export enum commissionLanguages {
    čeština = 'cs',
    angličtina = 'en',
    němčina = 'de',
}

const locationSchema = () =>
    z.object({
        city: z.string(),
        countryCode: z.string(),
        postalCode: z.string(),
        longitude: z.number(),
        latitude: z.number(),
    });

const sendInquirySchema = () =>
    z.object({
        to: z.array(z.object({ dispatcherId: z.number() })),
        body: z.object({
            cs: z.string(),
            en: z.string(),
            de: z.string(),
        }),
        parameters: z.object({
            minLength: z.number().nonnegative(),
            minHeight: z.number().nonnegative(),
            minWeight: z.number().nonnegative(),
            minWidth: z.number().nonnegative(),
            requiredFeatures: z.array(z.number()),
            requiredFeaturesSome: z.array(z.number()),
            vehicleTypes: z.array(z.number()),
        }),
        subject: z.string(),
        commissionId: z.number(),
    });

// createOrderCarrier - Objednávka na dopravce
const sendCreateOrderCarrierPdfSchema = () =>
    z.object({
        commissionId: z.number(),
        lang: z.string(),
        qid: z.string(),
        carrierDispatcherName: z.string(),
        carrierCompany: z.string(),
        carrierDispatcherGsm: z.string(),
        carrierStreet: z.string(),
        carrierDispatcherEmail: z.string(),
        carrierCountry: z.string(),
        carrierPostalCode: z.string(),
        carrierCity: z.string(),
        carrierDriver: z.string(),
        carrierRegistrationNumber: z.string(),
        carrierGsm: z.string(),
        carrierTaxNumber: z.string(),
        carrierRegistrationPlate: z.string(),
        loadings: z.array(
            z.object({
                loadingNumber: z.number(),
                loadingDate: z.string().min(1),
                loadingTime: z.string(),
                loadingCompany: z.string(),
                loadingStreet: z.string(),
                loadingCountry: z.string(),
                loadingPostalCode: z.string(),
                loadingCity: z.string(),
                loadingGps: z.string(),
                loadingRefNumber: z.string(),
            }),
        ),
        discharges: z.array(
            z.object({
                dischargeNumber: z.number(),
                dischargeDate: z.string().min(1),
                dischargeTime: z.string(),
                dischargeCompany: z.string(),
                dischargeStreet: z.string(),
                dischargeCountry: z.string(),
                dischargePostalCode: z.string(),
                dischargeCity: z.string(),
                dischargeGps: z.string(),
                neutralization: z.boolean(),
            }),
        ),
        items: z.array(
            z.object({
                itemLoading: z.string(),
                itemDischarge: z.string(),
                itemName: z.string(),
                itemPackage: z.string(),
                itemQuantity: z.string(),
                itemSize: z.string(),
                itemLoadingMeters: z.string(),
                itemWeight: z.string(),
                itemWeightBrutto: z.string(),
            }),
        ),
        disposition: z.string().min(1),
        carrierPrice: z.number(),
        orderCurrency: z.string(),
        date: z.string().min(1),
        createdBy: z.string(),
        createdByPhoneNumber: z.string(),
    });

const sendUniversalOrderEmailSchema = () =>
    z.object({
        to: z.string(),
        dispatcher: z.object({
            name: z.string(),
            surname: z.string(),
            phone: z.string(),
            email: z.string(),
        }),
        subject: z.string(),
        body: z.string(),
        qid: z.string(),
        lang: z.string(),
    });

// createLoadingConfirmation - Potvrzení nakládky
const sendCreateLoadingConfirmationPdfSchema = () =>
    z.object({
        lang: z.string(),
        qid: z.string(),
        loadingRefNumber: z.string(),
        customerCompany: z.string(),
        items: z.string(),
        loadingDate: z.string(),
        loadingTime: z.string(),
        carrierRegistrationPlate: z.string(),
        carrierDriver: z.string(),
        carrierGsm: z.string(),
    });

// createDeliveryConfirmation - Potvrzení doručení zboží
const sendCreateDeliveryConfirmationPdfSchema = () =>
    z.object({
        lang: z.string(),
        qid: z.string(),
        customerCompany: z.string(),
        items: z.string(),
        dischargeDate: z.string(),
        dischargeTime: z.string(),
        carrierRegistrationPlate: z.string(),
        carrierDriver: z.string(),
        carrierGsm: z.string(),
    });

// neutralization - Neutralizace
const sendCreateNeutralizationPdfSchema = () =>
    z.object({
        lang: z.string(),
        date: z.string(),
        qid: z.string(),
        dischargeNumber: z.number(),
        senderCompany: z.string(),
        senderStreet: z.string(),
        senderCountry: z.string(),
        senderPostalCode: z.string(),
        senderCity: z.string(),
        receiverCompany: z.string(),
        receiverStreet: z.string(),
        receiverCountry: z.string(),
        receiverPostalCode: z.string(),
        receiverCity: z.string(),
    });

const sendEmailSchema = () =>
    z.object({
        to: z.string(),
        dispatcher: z.object({
            name: z.string(),
            surname: z.string(),
            phone: z.string(),
            email: z.string(),
        }),
        body: z.string(),
        attachments: z.array(z.string()),
        lang: z.nativeEnum(commissionLanguages),
    });

export const commissionLoadingSchema = (t: T) =>
    z.object({
        // basic
        commissionLoading_id: z.number().optional(),
        commission_id: z.number().nullish(),
        date: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .min(1, { message: t('form.errors.required') }),
        dateTo: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .nullable(),
        timeFrom: zodTimeString(t('form.errors.timeFormat')),
        timeTo: zodTimeString(t('form.errors.timeFormat')),
        note: z.string().nullish(),
        refNumber: z
            .string({
                required_error: t('form.errors.required'),
            })
            .nullable(),
        location_id: z
            .number({
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .nullable(),
        location: locationCustomerSchema(t).partial().nullable(),

        number: z.number({}).nullable(),
    });

export const commissionDischargeSchema = (t: T) =>
    z.object({
        // basic
        commissionDischarge_id: z.number().optional(),
        commission_id: z.number().nullish(),
        date: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .min(1, { message: t('form.errors.required') }),
        dateTo: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .nullable(),
        timeFrom: zodTimeString(t('form.errors.timeFormat')),
        timeTo: zodTimeString(t('form.errors.timeFormat')),
        note: z.string().nullish(),
        neutralization: z.boolean(),
        location_id: z
            .number({
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .nullable(),
        location: locationCustomerSchema(t).partial().nullable(),

        number: z.number({}).nullable(),
    });

// Universal scheme for both loading and discharge
// const commissionLoadingDischargeSchema = (t: T) => commissionLoadingSchema(t).merge(commissionDischargeSchema(t));

export const commissionLoadingDischargeSchema = (t: T) =>
    commissionLoadingSchema(t)
        .merge(commissionDischargeSchema(t))
        .extend({
            isLoading: z.boolean(),
            neutralization: z.boolean().optional(),
            refNumber: z.string().nullish(),
        })
        .refine(
            (data) => {
                // The date range says on which days the stop can happen, the window says between
                // which times on such a day — so the range must not run backwards.
                if (!data.date || !data.dateTo) return true;
                return Number(data.dateTo) >= Number(data.date);
            },
            {
                message: t('form.errors.invalidDateSequence'),
                path: ['dateTo'],
            },
        )
        .refine(
            // The same window applies to every candidate day, which is what rules out an overnight
            // window: `Čas do` before `Čas od` would have to belong to the following day.
            (data) => isTimeWindowOrdered(data.timeFrom, data.timeTo),
            {
                message: t('form.errors.invalidDateSequence'),
                path: ['timeTo'],
            },
        );

const commissionGoodsSchema = (t: T) =>
    z.object({
        commissionItem_id: z.number().optional(),
        commissionLoading_id: z.number().nullable(),
        commissionDischarge_id: z.number().nullable(),
        // first row
        name: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .min(1, { message: t('form.errors.required') }),
        loadingIdx: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .min(1, { message: t('form.errors.required') }),
        dischargeIdx: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .min(1, { message: t('form.errors.required') }),
        quantity: z
            .number({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .nonnegative({ message: t('form.errors.minValue', { value: 0 }) })
            .nullable(),
        weight: z
            .number({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .nonnegative({ message: t('form.errors.minValue', { value: 0 }) })
            .nullable(),
        // second row
        weightBrutto: z
            .number({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .nonnegative({ message: t('form.errors.minValue', { value: 0 }) })
            .nullable(),
        packaging: z.string().nullable(),
        loadingMeters: z
            .number({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .nonnegative({ message: t('form.errors.minValue', { value: 0 }) })
            .nullable(),
        // third row - dimensions
        width: z
            .number({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .nonnegative({ message: t('form.errors.minValue', { value: 0 }) })
            .nullable(),
        length: z
            .number({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .nonnegative({ message: t('form.errors.minValue', { value: 0 }) })
            .nullable(),
        height: z
            .number({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .nonnegative({ message: t('form.errors.minValue', { value: 0 }) })
            .nullable(),
        note: z.string().nullable(),

        // the following properties does not exist when fetching from server, but are created manually
        commissionloading: commissionLoadingSchema(t).nullish(),
        commissiondischarge: commissionDischargeSchema(t).nullish(),
    });

export const commissionUserSchema = () => {
    return z.object({
        email: z.string(),
        jobTitle: z.string(),
        name: z.string(),
        surname: z.string(),
        number: z.number(),
        user_id: z.number(),
        username: z.string(),
    });
};

const basicCommissionSchema = (t: T) =>
    z.object({
        // basic information
        relation: z.string().optional(),
        week: z
            .number({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .min(1, { message: t('form.errors.required') }),
        year: z
            .number({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .min(1, { message: t('form.errors.required') }),
        // customer
        customer_id: z
            .number({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .nullable(),
        priceCustomer: z
            .number({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .nonnegative({ message: t('form.errors.minValue', { value: 0 }) })
            .nullable(),
        currencyCustomer: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .nullable(),
        invNumber: z
            .number({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .nullable(),
        orderNumber: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .nullable(),
        orderDate: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .min(1, { message: t('form.errors.required') }),
        customerContact_id: z
            .number({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .nullable(),
        // carrier assignment
        carrier_id: z
            .number({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .nullable(),
        dispatcher_id: z
            .number({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .nullable(),
        carrierDriver: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .nullable(),
        carrierGsm: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .nullable(),
        carrierRegistrationPlate: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .nullable(),
        priceCarrier: z
            .number({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.numberFormat'),
            })
            .nonnegative({ message: t('form.errors.minValue', { value: 0 }) })
            .nullable(),
        currencyCarrier: z.string(),
        // other
        disposition: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .nullable(),
        note: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .nullable(),
        notification: z.boolean(),
        // vehicle preferences
        minLoadingMeters: z.number().nullable(),
        minLoadingWeight: z.number().nullable(),
        vehicleTypes: z
            .array(
                z.object({
                    vehicleTypeId: z.number(),
                    selected: z.boolean(),
                }),
            )
            .optional(),
        vehicleFeatures: z
            .array(
                z.object({
                    vehicleFeatureId: z.number(),
                    selected: z.boolean(),
                }),
            )
            .optional(),
        // another
        commission_id: z.number().optional(),
        qid: z.string().nullish(),
        addedBy: commissionUserSchema().nullable(),
        addedBy_id: z.number().nullable(),
        carrierAssignedBy: commissionUserSchema().nullable(),
        carrierAssignedBy_id: z.number().nullable(),
        editedBy: z.string().optional(),
        invoice_id: z.number().nullable(),
        vat: z.enum(['0', '21']),
        exchangeRateCustomer: z
            .number()
            .nonnegative({ message: t('form.errors.minValue', { value: 0 }) })
            .nullable(),
        exchangeRateCarrier: z
            .number()
            .nonnegative({ message: t('form.errors.minValue', { value: 0 }) })
            .nullable(),
        state: z.nativeEnum(ECommissionState),
        // auxiliary properties
        totalloadingIdx: z.string().optional(),
        totaldischargeIdx: z.string().optional(),
        // when orderNumber is not "telefon" nor "e-mail", show this field
        fakeOrderNumber: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .optional(),
        // when priceCustomer is EUR, show these field
        priceCustomerInCZK: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .optional(),
        // when carrierCurrency is EUR, show these field
        priceCarrierInCZK: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .optional(),
        totalWeight: z.string().optional(),
        totalLoadingMeters: z.string().optional(),
    });

export const commissionSchema = (t: T) =>
    basicCommissionSchema(t).extend({
        // Goods
        commissionitem: z.array(commissionGoodsSchema(t)),
        priceEstimation: z
            .object({
                minPrice: z.number(),
                maxPrice: z.number(),
            })
            .nullable(),
        // the following properties does not exist when fetching from server, but are created manually
        commissionLoadingDischarge: z.array(commissionLoadingDischargeSchema(t)),
    });

export const commissionExportSchema = (t: T) => {
    const commonFields = {
        // document settings
        template: z.enum([
            'createOrderCarrier',
            'createOrderConfirmation',
            'createLoadingConfirmation',
            'createDeliveryConfirmation',
            'neutralization',
        ]),
        format: z.enum(['pdf', 'excel']),
        language: z.nativeEnum(commissionLanguages),
        companyName: z.string(),

        // email settings
        recipients: z
            .array(
                z.object({
                    value: z.string().email(),
                    label: z.string().email(),
                }),
            )
            .min(1)
            .max(100),
        subject: z.string().min(10, { message: t('form.errors.minLengthOther', { minLength: 10 }) }),
        message: z.string().min(1, { message: t('auth.email.error.required', { minLength: 10 }) }),
    };

    const earlyPayDiscountSchema = z
        .number({ coerce: true })
        .gt(0, { message: t('commissions.export.form.earlyPay.discountRange') })
        .lt(100, { message: t('commissions.export.form.earlyPay.discountRange') })
        .refine((value) => Math.abs(value * 100 - Math.round(value * 100)) < 1e-8, {
            message: t('commissions.export.form.earlyPay.twoDecimalPlaces'),
        });

    return z.discriminatedUnion('earlyPayOffered', [
        z.object({
            ...commonFields,
            earlyPayOffered: z.literal(false),
            earlyPayDiscount: z
                .unknown()
                .optional()
                .transform((value): number | undefined =>
                    typeof value === 'number' && Number.isFinite(value) ? value : undefined,
                ),
        }),
        z.object({
            ...commonFields,
            earlyPayOffered: z.literal(true),
            earlyPayDiscount: earlyPayDiscountSchema,
        }),
    ]);
};
export const commissionExportValidationSchema = (t: T) => {
    return commissionExportSchema(t).superRefine(({ recipients }, ctx) => {
        recipients.forEach((recipient, index) => {
            const valueEmailCheck = z.string().email();
            const labelEmailCheck = z.string().email();

            const valueResult = valueEmailCheck.safeParse(recipient.value);
            const labelResult = labelEmailCheck.safeParse(recipient.label);

            if (!valueResult.success || !labelResult.success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t('form.errors.itemOnPositionHasToBeEmail', { position: index + 1 }),
                    path: ['recipients'],
                });
            }
        });
    });
};

export const commissionsTableSchema = () =>
    z
        .object({
            addedBy: commissionUserSchema().nullable(),
            addedBy_id: z.number().nullable(),
            carrierAssignedBy: commissionUserSchema().nullable(),
            carrierAssignedBy_id: z.number().nullable(),
            commission_id: z.number(),
            state: z.nativeEnum(ECommissionState),
            relation: z.string(),
            week: z.number(),
            number: z.number().nullable(),
            year: z.number(),
            customer_company: z.string().nullable(),
            loading_date: z.string().nullable(),
            loading_city: z.array(z.string()).nullable(),
            loading_zip: z.array(z.string()).nullable(),
            discharge_date: z.string().nullable(),
            discharge_city: z.array(z.string()).nullable(),
            discharge_zip: z.array(z.string()).nullable(),
            total_weight: z.string().nullable(),
            total_loading_meters: z.string().nullable(),
            priceCustomer: z.string().nullable(),
            invNumber: z.string().nullish(),
            carrier_company: z.string().nullable(),
            priceCarrier: z.string().nullable(),
            provision: z.string().nullable(),
            notification: z.boolean(),
            note: z.string().nullable(),
            customer_id: z.number().nullable(),
            invoice_id: z.number().nullable(),
            carrier_id: z.number().nullable(),
            currency: z.string().optional(),
            currencyCarrier: z.string(),
            qid: z.string().nullable(),
            oldid: z.string().nullable(),
            enquiry_id: z.number().nullable(),
            enquiryState: z.nativeEnum(AdminOfferStatusValue).nullable(),
            priceEstimation: z
                .object({
                    minPrice: z.number(),
                    maxPrice: z.number(),
                })
                .nullable(),
            invoiceAttachment: attachmentSchema.nullable(),
        })
        .strict();

// used when fetching commission endpoint with invoiced = false parameter;
// this type uses "currency" and "number" instead of "currencyCustomer" and "commissionNumber"
export const notInvoicedCommission = () =>
    z.array(
        z.object({
            commission_id: z.number(),
            number: z.number(),
            orderDate: z.string(),
            loading_city_string: z.string(),
            discharge_city_string: z.string(),
            vat: z.string(),
            priceCustomerOriginal: z.string(),
            currency: z.nativeEnum(Currency),
            customer_company: z.string(),
            // used to fetch customer and then get defaultDueNumber
            customer_id: z.number(),
            loading_date: z.array(z.string()),
            discharge_date: z.array(z.string()),
            qid: z.string().optional(),
            state: z.nativeEnum(ECommissionState),
        }),
    );

export const commissionsPageSchema = () =>
    z.object({
        data: z.array(commissionsTableSchema()),
        totalRows: z.number(),
    });

export const commissionSummarySchema = () =>
    z.object({
        totalPriceCustomer: z.number(),
        totalPriceCarrier: z.number(),
        totalProvision: z.number(),
    });

export type LocationSchema = z.infer<ReturnType<typeof locationSchema>>;
export type SendInquirySchema = z.infer<ReturnType<typeof sendInquirySchema>>;
export type SendUniversalOrderEmailSchema = z.infer<ReturnType<typeof sendUniversalOrderEmailSchema>>;
export type SendCreateOrderCarrierPdfSchema = z.infer<ReturnType<typeof sendCreateOrderCarrierPdfSchema>>;
export type SendCreateLoadingConfirmationPdfSchema = z.infer<ReturnType<typeof sendCreateLoadingConfirmationPdfSchema>>;
export type SendCreateDeliveryConfirmationPdfSchema = z.infer<
    ReturnType<typeof sendCreateDeliveryConfirmationPdfSchema>
>;
export type SendCreateNeutralizationPdfSchema = z.infer<ReturnType<typeof sendCreateNeutralizationPdfSchema>>;
export type SendEmailSchema = z.infer<ReturnType<typeof sendEmailSchema>>;
export type CommissionLoadingSchema = z.infer<ReturnType<typeof commissionLoadingSchema>>;
export type CommissionDischargeSchema = z.infer<ReturnType<typeof commissionDischargeSchema>>;
export type CommissionLoadingDischargeSchema = z.infer<ReturnType<typeof commissionLoadingDischargeSchema>>;
export type CommissionGoodsSchema = z.infer<ReturnType<typeof commissionGoodsSchema>>;
export type BasicCommissionSchema = z.infer<ReturnType<typeof basicCommissionSchema>>;
export type CommissionSchema = z.infer<ReturnType<typeof commissionSchema>>;
export type CommissionExportSchema = z.infer<ReturnType<typeof commissionExportSchema>>;
export type CommissionsTableSchema = z.infer<ReturnType<typeof commissionsTableSchema>>;
export type NotInvoicedCommission = z.infer<ReturnType<typeof notInvoicedCommission>>;
export type CommissionsPageSchema = z.infer<ReturnType<typeof commissionsPageSchema>>;
export type CommissionSummarySchema = z.infer<ReturnType<typeof commissionSummarySchema>>;
export type TCommissionUserSchema = z.infer<ReturnType<typeof commissionUserSchema>>;

export * from './api';
export * from './createCommissionRequest';
